import { Box, Chip } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { SectionRequirementProvider } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import {
  REQUIRED_SECTION_COLORS,
  REQUIRED_SECTION_ELEMENT_ID_PREFIX,
  REQUIRED_SECTION_LABELS,
  REQUIRED_SECTION_SCROLL_OFFSET_PX,
} from "../initial-f2f-required-constant";

export interface RequiredSectionFrameProps {
  sectionKey: string;
  isRequired: boolean;
  /** Found empty at the last Save & Sign; stays until the next attempt. */
  isMissing: boolean;
  children: ReactNode;
}

// The accordion summary row is 30px high; the chip sits on it without touching the section components.
const SUMMARY_ROW_HEIGHT_PX = 30;

/** Wraps a section with its Required chip, outlined in red when the last Save & Sign found it empty. */
const RequiredSectionFrame = ({
  sectionKey,
  isRequired,
  isMissing,
  children,
}: RequiredSectionFrameProps) => {
  const requirement = useMemo(
    () => ({ isRequired, isMissing }),
    [isRequired, isMissing]
  );
  return (
    <Box
      id={`${REQUIRED_SECTION_ELEMENT_ID_PREFIX}${sectionKey}`}
      sx={{
        position: "relative",
        scrollMarginTop: REQUIRED_SECTION_SCROLL_OFFSET_PX,
        borderRadius: 1,
        border: `1px solid ${isMissing ? REQUIRED_SECTION_COLORS.required : "transparent"}`,
        bgcolor: isMissing
          ? REQUIRED_SECTION_COLORS.requiredBackground
          : undefined,
      }}
    >
      <SectionRequirementProvider value={requirement}>
        {children}
      </SectionRequirementProvider>
      {isRequired && (
        <Chip
          size="small"
          variant="outlined"
          label={REQUIRED_SECTION_LABELS.required}
          sx={{
            position: "absolute",
            top: (SUMMARY_ROW_HEIGHT_PX - 20) / 2,
            right: 48,
            height: 20,
            pointerEvents: "none",
            bgcolor: "#FFFFFF",
            color: isMissing
              ? REQUIRED_SECTION_COLORS.required
              : REQUIRED_SECTION_COLORS.neutralChip,
            borderColor: isMissing
              ? REQUIRED_SECTION_COLORS.required
              : REQUIRED_SECTION_COLORS.neutralBorder,
          }}
        />
      )}
    </Box>
  );
};

export default RequiredSectionFrame;
